import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { printPackageReceipt } from '../components/PackageReceiptPrintService';
import { yemeksepetiApi } from '../../../../config/apiClient';
import { translateYsDiscountName, translateYsSponsor } from '../utils/helpers';
import { buildYemeksepetiReceiptItems, parseYsRawJsonData, buildGoogleMapsUrl } from '../utils/yemeksepetiReceiptBuilder';
import { getYsDeliveryKind, getYsDeliveryLabel, formatYsPickupTime } from '../utils/yemeksepetiDelivery';
import { resolveReceiptHeader, resolveReceiptLabel, buildReceiptOrderRef, toDisplaySettings } from '../utils/receiptHeader';
import type { PlatformIntegration } from '../../../../types/platformIntegration';
import QRCode from 'qrcode';

interface YsOrderDetail {
  id: number;
  token: string;
  code: string;
  remoteOrderId: string;
  shortCode?: string;
  status: string;
  orderType?: string;
  expeditionType?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerFullName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerComment?: string;
  deliveryAddress?: string;
  deliveryCity?: string;
  deliveryDistrict?: string;
  paymentStatus?: string;
  paymentType?: string;
  grandTotal: number;
  // Yemeksepeti v2026: indirim oncesi net toplam (topping/servis dahil). Backend
  // kolonu (price.totalNet'ten dolduruluyor); eski siparislerde 0 olabilir, o
  // zaman ham JSON'a fallback. Bkz. getYsTotals().
  totalNet?: number;
  vatTotal?: number;
  payRestaurant: number;
  collectFromCustomer: number;
  companyId?: string;
  branchId?: string;
  assignedCourierId?: number;
  assignedCourierName?: string;
  createdAt: string;
  updatedAt?: string;
  items?: YsOrderItemDetail[];
  rawJsonData?: string;
  isPreOrder?: boolean;
  expectedDeliveryTime?: string;
  // Yemeksepeti v2026 (15.05.2026): order-level indirimler (sponsorship dahil)
  // backend tarafinda JSON serialize edilmis kolon. Eski siparislerde null gelir.
  orderDiscountsJson?: string | null;
}

interface YsOrderItemDetail {
  id: number;
  name: string;
  quantity: number;
  unitPrice: number;
  paidPrice: number;
  comment?: string;
  categoryName?: string;
  variationName?: string;
  toppingsJson?: string;
  // Yemeksepeti v2026: urun-level indirimler (sponsorship dahil) JSON serialize.
  discountsJson?: string | null;
  // Yemeksepeti v2026: indirim oncesi orijinal birim fiyat. Yoksa unitPrice fallback.
  originalUnitPrice?: number | null;
}

// Yemeksepeti v2026 (15.05.2026): Sponsor enum + sponsorship breakdown
// PLATFORM = Yemeksepeti, VENDOR = Restoran, THIRD_PARTY = 3. Taraf
export type DiscountSponsor = 'PLATFORM' | 'VENDOR' | 'THIRD_PARTY' | string;
export interface SponsorshipBreakdown {
  sponsor: DiscountSponsor;
  amount: number;
}
// Yemeksepeti v2026: Indirim adlari sadece 3 sabit baslik altinda gelir:
// "Food Discount" | "Delivery Fee Discount" | "Loyalty"
export interface DiscountWithSponsorship {
  name: string;
  amount: number;
  sponsorships?: SponsorshipBreakdown[];
}

interface RawProductOption {
  id?: string;
  name: string;
  price?: string | number;
  quantity?: number;
  type?: string;
  children?: RawProductOption[];
  // Yemeksepeti v2026: Topping seviyesi indirim. Eski payload'da undefined.
  discounts?: DiscountWithSponsorship[];
}

interface RawProduct {
  id?: string;
  name: string;
  unitPrice?: string | number;
  paidPrice?: string | number;
  quantity?: string | number;
  comment?: string;
  variation?: { name?: string };
  selectedToppings?: RawProductOption[];
  selectedChoices?: RawProductOption[];
  // Yemeksepeti v2026: Urun-level indirim (sponsorship breakdown dahil).
  // Eski payload'da yoktur (undefined kalir).
  discounts?: DiscountWithSponsorship[];
}

interface RawDiscount extends DiscountWithSponsorship {
  discountType?: string;
}

// Defansif normalize: hem yeni v2026 (camelCase) hem .NET PascalCase varyantlarini
// destekler. Eksik/0 sponsorship kayitlari filtrelenir.
const normalizeDiscount = (d: any): RawDiscount => {
  const sponsorshipsRaw: any[] = Array.isArray(d?.sponsorships)
    ? d.sponsorships
    : Array.isArray(d?.Sponsorships)
      ? d.Sponsorships
      : [];
  const sponsorships: SponsorshipBreakdown[] = sponsorshipsRaw
    .map((s: any) => ({
      sponsor: (s?.sponsor || s?.Sponsor || '').toString(),
      amount: parseFloat(s?.amount ?? s?.Amount ?? '0') || 0,
    }))
    .filter((s) => s.sponsor && s.amount !== 0);
  return {
    name: d?.name || d?.Name || 'Indirim',
    amount: parseFloat(d?.amount ?? d?.Amount ?? d?.discountAmount ?? '0') || 0,
    discountType: d?.discountType || d?.DiscountType || '',
    sponsorships: sponsorships.length > 0 ? sponsorships : undefined,
  };
};

// Topping/secim recursive normalize: discounts varsa v2026 sponsorship breakdown
// cikarilir; children da rekursif islenir.
const normalizeOption = (opt: any): RawProductOption => ({
  ...opt,
  discounts: Array.isArray(opt?.discounts) && opt.discounts.length > 0
    ? opt.discounts.map(normalizeDiscount)
    : undefined,
  children: Array.isArray(opt?.children)
    ? opt.children.map((c: any) => normalizeOption(c))
    : undefined,
});

const parseRawJsonData = (rawJson?: string): {
  deliveryInstructions?: string;
  products: RawProduct[];
  customerComment?: string;
  discounts: RawDiscount[];
} => {
  if (!rawJson) return { products: [], discounts: [] };
  try {
    const data = JSON.parse(rawJson);
    const deliveryInstructions = data?.delivery?.address?.deliveryInstructions;
    // Urun ve topping seviyesindeki discounts'u v2026 yapisina normalize et.
    const products: RawProduct[] = (data?.products || []).map((p: any): RawProduct => ({
      ...p,
      discounts: Array.isArray(p?.discounts) && p.discounts.length > 0
        ? p.discounts.map(normalizeDiscount)
        : undefined,
      selectedToppings: Array.isArray(p?.selectedToppings)
        ? p.selectedToppings.map((t: any) => normalizeOption(t))
        : undefined,
      selectedChoices: Array.isArray(p?.selectedChoices)
        ? p.selectedChoices.map((t: any) => normalizeOption(t))
        : undefined,
    }));
    const customerComment = data?.comments?.customerComment;
    const discounts: RawDiscount[] = (data?.discounts || []).map(normalizeDiscount);
    return { deliveryInstructions, products, customerComment, discounts };
  } catch {
    return { products: [], discounts: [] };
  }
};

// Backend-first: Yeni siparislerde structured kolon dolu gelir, eski siparislerde
// fallback olarak raw JSON'dan parse edilir. Boylece 15.05 oncesi/sonrasi tutarli.
const getOrderDiscounts = (
  order: { orderDiscountsJson?: string | null; rawJsonData?: string },
): DiscountWithSponsorship[] => {
  if (order.orderDiscountsJson) {
    try {
      const parsed = JSON.parse(order.orderDiscountsJson);
      if (Array.isArray(parsed)) return parsed.map(normalizeDiscount);
    } catch { /* fallthrough */ }
  }
  return parseRawJsonData(order.rawJsonData).discounts;
};

const getItemDiscounts = (
  item: { discountsJson?: string | null },
  rawProd?: RawProduct,
): DiscountWithSponsorship[] => {
  if (item.discountsJson) {
    try {
      const parsed = JSON.parse(item.discountsJson);
      if (Array.isArray(parsed)) return parsed.map(normalizeDiscount);
    } catch { /* fallthrough */ }
  }
  return rawProd?.discounts || [];
};

// Yemeksepeti v2026 (15.05.2026) ile birlikte products[].paidPrice semantigi
// degisti: artik satir toplami degil, sadece urun baz fiyati (topping haric).
// Bizim eski hesabimiz `itemsTotal = sum(paidPrice)` v2026 sonrasi siparislerde
// topping ucretini disarda biraktigi icin yanlis sonuc veriyor.
//
// `getYsTotals` versiyon ayrimi yapmadan calisir: 3 katmanli fallback
// hiyerarsisinde Yemeksepeti'nin kendi nihai hesabi olan `price` objesini
// birinci oncelikli kaynak kabul eder. Hem v2026 oncesi hem sonrasi siparisler
// icin tek kod yolu uretir.
interface YsTotals {
  araToplam: number;
  indirimToplam: number;
  genelToplam: number;
  source: 'backend' | 'rawJson' | 'computed';
}

const getYsTotals = (order: {
  totalNet?: number;
  grandTotal?: number;
  rawJsonData?: string;
  items?: { paidPrice?: number }[];
}): YsTotals => {
  const grandTotal = Number(order.grandTotal ?? 0) || 0;

  // Katman 1: Backend kolonu (her iki formatta da dolu — v2026 raporu 3.1)
  const backendTotalNet = Number(order.totalNet ?? 0) || 0;
  if (backendTotalNet > 0.001) {
    return {
      araToplam: backendTotalNet,
      indirimToplam: Math.max(0, backendTotalNet - grandTotal),
      genelToplam: grandTotal,
      source: 'backend',
    };
  }

  // Katman 2: Ham JSON `price` objesi (Yemeksepeti'nin kendi hesabi)
  if (order.rawJsonData) {
    try {
      const data = JSON.parse(order.rawJsonData);
      const rawTotalNet = parseFloat(data?.price?.totalNet ?? '0') || 0;
      const rawGrandTotal = parseFloat(data?.price?.grandTotal ?? '0') || grandTotal;
      const rawDiscount = parseFloat(data?.price?.discountAmountTotal ?? '0') || 0;
      if (rawTotalNet > 0.001 || rawGrandTotal > 0.001) {
        const ara = rawTotalNet > 0.001 ? rawTotalNet : rawGrandTotal + rawDiscount;
        return {
          araToplam: ara,
          indirimToplam: rawDiscount > 0.001 ? rawDiscount : Math.max(0, ara - rawGrandTotal),
          genelToplam: rawGrandTotal,
          source: 'rawJson',
        };
      }
    } catch { /* fallthrough */ }
  }

  // Katman 3: Fallback - sum(paidPrice) (eski format icin dogru, yeni format
  // icin topping eksik ama hicbir kaynak yoksa son care).
  const sumPaid = (order.items || []).reduce((s, it) => s + (Number(it.paidPrice) || 0), 0);
  return {
    araToplam: sumPaid,
    indirimToplam: Math.max(0, sumPaid - grandTotal),
    genelToplam: grandTotal,
    source: 'computed',
  };
};

// Topping fiyatlarinin item.paidPrice icine dahil olup olmadigini order
// seviyesinde tek seferde tespit eder. `sum(paidPrice)` mu `getYsTotals.araToplam`'a
// daha yakin yoksa `sum(paidPrice + topping*qty)` mu — hangisi daha yakinsa o
// yorum kabul edilir. Yuvarlama farklarina karsi epsilon dayanikli.
//
// - Eski format (v2026 oncesi): paidPrice zaten satir toplami → `true` doner,
//   urun satirlari oldugu gibi gosterilir.
// - Yeni format (v2026): paidPrice topping haric → `false` doner, satir
//   toplami `paidPrice + topping*qty` olarak hesaplanip gosterilir.
const detectPaidPriceIncludesToppings = (
  order: { totalNet?: number; grandTotal?: number; rawJsonData?: string; items?: any[] },
): boolean => {
  const items = order.items || [];
  if (items.length === 0) return true;

  const sumPaid = items.reduce((s: number, it: any) => s + (Number(it.paidPrice) || 0), 0);
  const sumToppings = items.reduce((s: number, it: any) => {
    const dbToppings: any[] = (() => {
      try { return it.toppingsJson ? JSON.parse(it.toppingsJson) : []; } catch { return []; }
    })();
    const toppingTotal = dbToppings.reduce(
      (tt: number, t: any) => tt + (parseFloat(t?.price ?? '0') || 0) * (Number(t?.quantity) || 1),
      0,
    );
    return s + toppingTotal * (Number(it.quantity) || 1);
  }, 0);

  // Topping yok → her iki yorum ayni → mevcut davranis (true) korunur.
  if (sumToppings < 0.01) return true;

  const totals = getYsTotals(order);
  // Hicbir guvenilir referans yoksa eski format varsayimi (default true).
  if (totals.source === 'computed' || totals.araToplam < 0.01) return true;

  const distOldFormat = Math.abs(sumPaid - totals.araToplam);
  const distNewFormat = Math.abs(sumPaid + sumToppings - totals.araToplam);
  return distOldFormat <= distNewFormat;
};

// Yemeksepeti v2026 - translateYsDiscountName ve translateYsSponsor utils/helpers'dan
// import edilir (bkz. asagidaki import). Bu dosyada lokal kopya tutulmaz.

export interface YemeksepetiOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number | null;
  companyId: string;
  /**
   * Online ödenmiş siparişler için Pavo cihazından mali fiş kestirme handler'i.
   * Tanimliysa modaldaki "Online Fis Yazdir" butonu aktif olur (sadece online
   * odenmis siparislerde gorunur). Tiklandiginda parent REST-P2P cihaz secim
   * modalini acar.
   */
  onRequestOnlineReceipt?: () => void;
  screenData?: {
    id: number;
    localServiceUrl?: string;
    printingSettings?: {
      printingMode: string;
      defaultReceiptPrinter?: { id: string; name: string } | null;
      adisyonPrinter?: { id: string; name: string } | null;
      receiptDisplaySettings?: {
        companyName?: string;
        companyAddress?: string;
        taxNumber?: string;
        headerType?: 'text' | 'logo';
        logoUrl?: string;
        logoMaxWidthPx?: number;
        logoMaxHeightPx?: number;
        standardFooterNote?: string;
        thankYouMessage?: string;
      };
    };
    /** Marka bazlı fiş başlığını çözebilmek için entegrasyon listesi. */
    platformIntegrations?: PlatformIntegration[];
  };
}

const getStatusLabel = (status?: string): { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' } => {
  const s = (status || '').toUpperCase();
  if (s === 'PENDING' || s === 'NEW') return { label: 'Onay Bekleniyor', color: 'warning' };
  if (s === 'ACCEPTED' || s === 'CONFIRMED') return { label: 'Onaylandi', color: 'info' };
  if (s === 'PREPARED' || s === 'READY') return { label: 'Hazirlandi', color: 'success' };
  if (s === 'PICKED_UP' || s === 'ON_DELIVERY' || s === 'DELIVERY') return { label: 'Yolda', color: 'info' };
  if (s === 'DELIVERED' || s === 'COMPLETED') return { label: 'Teslim Edildi', color: 'success' };
  if (s === 'REJECTED') return { label: 'Reddedildi', color: 'error' };
  if (s === 'CANCELLED') return { label: 'Iptal Edildi', color: 'error' };
  return { label: status || 'Bilinmiyor', color: 'default' };
};

/**
 * Backend bazen UTC datetime'i 'Z' suffix OLMADAN (DateTimeKind.Unspecified)
 * gonderiyor. Bu durumda `new Date()` bunu tarayicinin LOKAL zamani kabul
 * ederek parse ediyor ve 3 saat ileride/geride degerler uretiyor. Burada
 * suffix yoksa 'Z' ekleyip sonucu her zaman Europe/Istanbul dilimine
 * formatliyoruz — boylece Yemeksepeti siparisinin UTC'de saklanan zamani
 * ekranda GMT+3 olarak dogru gorunuyor.
 */
const parseAsUtcDate = (dateStr?: string): Date | null => {
  if (!dateStr) return null;
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(dateStr);
  const normalized = hasTimezone ? dateStr : `${dateStr}Z`;
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
};

const ISTANBUL_TZ = 'Europe/Istanbul';

const formatDate = (dateStr?: string): string => {
  const d = parseAsUtcDate(dateStr);
  if (!d) return '-';
  try {
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, timeZone: ISTANBUL_TZ,
    }).format(d);
  } catch { return dateStr || '-'; }
};

const formatTime = (dateStr?: string): string => {
  const d = parseAsUtcDate(dateStr);
  if (!d) return '-';
  try {
    return new Intl.DateTimeFormat('tr-TR', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: ISTANBUL_TZ,
    }).format(d);
  } catch { return '-'; }
};

const formatDateOnly = (dateStr?: string): string => {
  const d = parseAsUtcDate(dateStr);
  if (!d) return '-';
  try {
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      timeZone: ISTANBUL_TZ,
    }).format(d);
  } catch { return '-'; }
};

const YemeksepetiOrderDetailModal: React.FC<YemeksepetiOrderDetailModalProps> = ({
  open,
  onClose,
  orderId,
  companyId,
  screenData,
  onRequestOnlineReceipt,
}) => {
  const [order, setOrder] = useState<YsOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (open && orderId && companyId) {
      const fetchOrderDetail = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await yemeksepetiApi.get(`/api/ys-orders/order/${orderId}`);
          const result = response.data;
          const resSuccess = result.Success ?? result.success;
          const resData = result.Data ?? result.data;
          const resMessage = result.Message ?? result.message;
          if (resSuccess && resData) {
            setOrder(resData);
          } else {
            setError(resMessage || 'Siparis detayi yuklenemedi');
          }
        } catch (err) {
          setError('Siparis detayi yuklenirken hata olustu');
          console.error('[YSDetail] Yukleme hatasi:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchOrderDetail();
    } else {
      setOrder(null);
    }
  }, [open, orderId, companyId]);

  const createYsReceiptHtml = useCallback((o: YsOrderDetail): string => {
    const receiptSettings = toDisplaySettings(
      resolveReceiptHeader(buildReceiptOrderRef('yemeksepeti', null, o), screenData),
    );
    const items = o.items || [];
    // 'Z' eksik gelebilecegi icin parseAsUtcDate araciligiyla normalize et.
    const orderDate = parseAsUtcDate(o.createdAt) ?? new Date();
    const dateStr = new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ISTANBUL_TZ,
    }).format(orderDate);
    const timeStr = new Intl.DateTimeFormat('tr-TR', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: ISTANBUL_TZ,
    }).format(orderDate);
    const ptUpper = (o.paymentType || '').toUpperCase().replace(/İ/g, 'I').replace(/Ş/g, 'S').replace(/Ğ/g, 'G');
    const isOnline = ptUpper.includes('ONLINE') ||
      ptUpper.includes('PAY_WITH_CARD') ||
      ptUpper.startsWith('PAY_WITH');
    const deliveryKind = getYsDeliveryKind(o as any);
    const isPickupOrder = deliveryKind === 'pickup';
    const deliveryLabel = getYsDeliveryLabel(o as any, 'receipt');
    const pickupTimeStr = isPickupOrder
      ? formatYsPickupTime(parseYsRawJsonData(o.rawJsonData).pickupTime)
      : undefined;
    const paymentLabel = isOnline ? 'Online Odeme' : (o.paymentType || 'Kapida Odeme');

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Yemeksepeti Siparis Fisi</title>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Poppins', sans-serif; font-size: 9pt; width: 72mm; max-width: 72mm; margin: 0; padding: 1mm 2mm 2mm 2mm; line-height: 1.2; color: #000; }
      .header { text-align: center; margin-bottom: 3px; }
      .header h2 { margin: 0 0 2px 0; }
      .header img { max-width: 50mm; max-height: 18mm; }
      .ys-badge { text-align: center; margin: 5px 0; padding: 4px 0; font-weight: bold; font-size: 12pt; letter-spacing: 3px; border-top: 2px solid #000; border-bottom: 2px solid #000; color: #e31e24; }
      .order-number { font-size: 14pt; text-align: center; margin: 5px 0 8px 0; font-weight: bold; letter-spacing: 2px; }
      .customer-info { font-size: 10pt; margin: 5px 0; padding: 4px; border: 1px solid #000; }
      .customer-info p { margin: 2px 0; }
      .customer-name { font-weight: bold; font-size: 11pt; }
      .customer-address { font-size: 9pt; margin-top: 3px; padding: 3px; border: 1px dashed #000; }
      .invoice-details { margin-bottom: 3px; font-size: 8pt; }
      .invoice-details p { margin: 0 0 1px 0; }
      .items-table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 2px; table-layout: fixed; }
      .items-table th, .items-table td { text-align: left; padding: 1px 0; }
      .items-table th { border-bottom: 1px solid #000; }
      .option-row td { font-size: 7pt; padding-left: 8px; }
      .summary { margin-top: 5px; text-align: right; font-size: 9pt; }
      .summary p { margin: 0 0 2px 0; }
      .grand-total { font-size: 13pt; font-weight: bold; text-align: center; margin: 8px 0; padding: 5px 0; }
      .order-note { font-size: 8pt; margin: 5px 0; padding: 3px; border: 1px dashed #000; }
      .delivery-type { text-align: center; font-size: 9pt; font-weight: bold; padding: 3px 0; margin: 4px 0; border-bottom: 1px dashed #000; }
      .pickup-box { text-align: center; font-size: 11pt; font-weight: bold; letter-spacing: 1px; padding: 5px 0; margin: 5px 0; border: 2px solid #000; }
      .pickup-time { font-size: 9pt; font-weight: normal; letter-spacing: 0; }
      .payment-info { text-align: center; font-size: 10pt; font-weight: bold; padding: 4px 0; margin: 5px 0; border: 1px solid #000; }
      .receipt-label { font-size: 10pt; text-align: center; margin: 8px 0; font-weight: bold; border: 1px solid #000; padding: 3px; letter-spacing: 2px; }
      .footer { margin-top: 5px; text-align: center; font-size: 8pt; border-top: 1px dashed #000; padding-top: 3px; }
      .right { text-align: right; }
      .bold { font-weight: bold; }
    </style></head><body>
      <div class="header">
        ${receiptSettings?.headerType === 'logo' && receiptSettings?.logoUrl
          ? `<img src="${receiptSettings.logoUrl}" alt="Logo" />`
          : `<h2>${receiptSettings?.companyName || 'AQUASOFT'}</h2>`}
      </div>
      ${receiptSettings?.headerType === 'logo' && receiptSettings?.logoUrl ? '' : `
        <div style="text-align:center;font-size:8pt;">
          ${receiptSettings?.companyAddress ? `<p style="margin:0 0 1px 0">${receiptSettings.companyAddress}</p>` : ''}
          ${receiptSettings?.taxNumber ? `<p style="margin:0 0 1px 0">VKN: ${receiptSettings.taxNumber}</p>` : ''}
        </div>
      `}
      <div class="ys-badge">* YEMEKSEPETI SIPARISI *</div>
      <div class="order-number">SIPARIS: ${o.shortCode || o.code || o.id}</div>
      <div class="customer-info">
        <p class="customer-name">${o.customerFullName || 'Musteri'}</p>
        ${o.customerPhone ? `<p>Tel: ${o.customerPhone}</p>` : ''}
        ${o.deliveryAddress ? `<div class="customer-address">${[o.deliveryAddress, o.deliveryDistrict, o.deliveryCity].filter(Boolean).join(', ')}</div>` : ''}
      </div>
      <div class="invoice-details">
        <p><span class="bold">Tarih:</span> ${dateStr} ${timeStr}</p>
        <p><span class="bold">Odeme:</span> ${paymentLabel}</p>
      </div>
      ${isPickupOrder
        ? `<div class="pickup-box">GEL AL - MUSTERI TESLIM ALACAK${pickupTimeStr ? `<div class="pickup-time">Teslim Alma Saati: ${pickupTimeStr}</div>` : ''}</div>`
        : `<div class="delivery-type">${deliveryLabel}</div>`}
      ${o.customerComment ? `<div class="order-note"><b>Siparis Notu:</b> ${o.customerComment}</div>` : ''}
      ${(() => {
        const raw = parseRawJsonData(o.rawJsonData);
        const dlvInstr = raw.deliveryInstructions;
        if (dlvInstr && dlvInstr !== o.customerComment) {
          return `<div class="order-note" style="border:2px dashed #e31e24;margin-top:3px;"><b>TESLIMAT TALIMATI:</b> ${dlvInstr}</div>`;
        }
        return '';
      })()}
      <table class="items-table">
        <thead><tr>
          <th style="width:45%;">Urun</th>
          <th class="right" style="width:12%;">Adet</th>
          <th class="right" style="width:20%;">Fiyat</th>
          <th class="right" style="width:23%;">Tutar</th>
        </tr></thead>
        <tbody>
          ${(() => {
            const raw = parseRawJsonData(o.rawJsonData);
            // v2026: Item-level + topping-level indirim satirlari
            const renderDiscountRows = (
              discounts: DiscountWithSponsorship[] | undefined,
              indentSpaces: number,
              labelPrefix: string,
            ): string => {
              if (!discounts || discounts.length === 0) return '';
              const indent = '&nbsp;'.repeat(indentSpaces);
              return discounts.map((d) => {
                const sponsorshipsHtml = (d.sponsorships && d.sponsorships.length > 0)
                  ? `<tr class="option-row"><td colspan="4" style="color:#666;font-size:6.5pt;">${indent}&nbsp;&nbsp;${d.sponsorships.map((s) => `${translateYsSponsor(s.sponsor)}: -${(s.amount || 0).toFixed(2)}`).join(' | ')}</td></tr>`
                  : '';
                return `<tr class="option-row"><td colspan="4" style="color:#c62828;">${indent}${labelPrefix}: ${translateYsDiscountName(d.name)} -${(d.amount || 0).toFixed(2)} TL</td></tr>${sponsorshipsHtml}`;
              }).join('');
            };
            const renderOptionRows = (opt: any, depth: number = 0): string => {
              const name = opt.name || opt.Name || '';
              const price = parseFloat(opt.price ?? opt.Price ?? '0');
              const qty = opt.quantity || opt.Quantity || 1;
              const indent = '&nbsp;'.repeat(depth * 3);
              let html = `<tr class="option-row"><td colspan="4">${indent}${depth > 0 ? '  ' : ''}${qty > 1 ? qty + 'x ' : ''}+ ${name}${price > 0 ? ` (+${price.toFixed(2)} TL)` : ''}</td></tr>`;
              // v2026: Topping-level indirim
              const optDiscountsRaw: any[] = Array.isArray(opt.discounts)
                ? opt.discounts
                : Array.isArray(opt.Discounts)
                  ? opt.Discounts
                  : [];
              if (optDiscountsRaw.length > 0) {
                html += renderDiscountRows(optDiscountsRaw.map(normalizeDiscount), depth * 3 + 3, 'Indirim');
              }
              const children: any[] = opt.children || opt.Children || [];
              children.forEach((child: any) => { html += renderOptionRows(child, depth + 1); });
              return html;
            };
            // v2026 uyum: paidPrice topping iceriyor mu? Order seviyesinde
            // tek seferde karar veriyoruz (modal ile ayni heuristik).
            const includesToppings = detectPaidPriceIncludesToppings(o);
            return items.map((item, idx) => {
              const rawProd = raw.products[idx];
              // v2026: Orijinal birim fiyat (eski siparislerde unitPrice'a fallback)
              const originalUnit = item.originalUnitPrice ?? item.unitPrice ?? 0;
              const dbToppings: any[] = (() => { try { return item.toppingsJson ? JSON.parse(item.toppingsJson) : []; } catch { return []; } })();
              const rawToppings = rawProd?.selectedToppings || [];
              const toppings = dbToppings.length > 0 ? dbToppings : rawToppings;
              // v2026 yeni format: paidPrice topping haric → topping*qty ekle.
              const toppingPerProduct = (toppings as any[]).reduce(
                (s: number, t: any) =>
                  s + (parseFloat(t?.price ?? '0') || 0) * (Number(t?.quantity) || 1),
                0,
              );
              const lineTotal = includesToppings
                ? (item.paidPrice || 0)
                : (item.paidPrice || 0) + toppingPerProduct * (item.quantity || 1);
              let rows = `<tr>
                <td>${item.name}</td>
                <td class="right">${item.quantity}</td>
                <td class="right">${originalUnit.toFixed(2)}</td>
                <td class="right">${lineTotal.toFixed(2)}</td>
              </tr>`;
              if (item.variationName && item.variationName !== item.name) {
                rows += `<tr class="option-row"><td colspan="4" style="font-style:italic;">${item.variationName}</td></tr>`;
              }
              toppings.forEach((t: any) => { rows += renderOptionRows(t); });
              const rawChoices = rawProd?.selectedChoices || [];
              if (rawChoices.length > 0) {
                rows += `<tr class="option-row"><td colspan="4" style="font-weight:bold;">Secimler:</td></tr>`;
                rawChoices.forEach((c: any) => { rows += renderOptionRows(c); });
              }
              // v2026: Urun-level indirim + sponsorship breakdown
              const itemDiscounts = getItemDiscounts(item, rawProd);
              if (itemDiscounts.length > 0) {
                rows += renderDiscountRows(itemDiscounts, 3, 'Indirim');
              }
              const comment = item.comment || rawProd?.comment;
              if (comment) {
                rows += `<tr class="option-row"><td colspan="4" style="color:#e65100;">Not: ${comment}</td></tr>`;
              }
              return rows;
            }).join('');
          })()}
        </tbody>
      </table>
      ${(() => {
        // v2026: Order-level indirim toplami + sponsorship breakdown ozeti
        const orderDiscounts = getOrderDiscounts(o);
        if (orderDiscounts.length === 0) return '';
        // v2026 uyum: payload'in `price` objesinden (3 katmanli fallback ile)
        // dogru ara toplami al — hem eski hem yeni format icin tutarli.
        const totals = getYsTotals(o);
        let html = `<div class="summary"><p><b>Ara Toplam:</b> ${totals.araToplam.toFixed(2)} TL</p>`;
        orderDiscounts.forEach((d) => {
          html += `<p style="color:#c62828;"><b>${translateYsDiscountName(d.name)}:</b> -${(d.amount || 0).toFixed(2)} TL</p>`;
          if (d.sponsorships && d.sponsorships.length > 0) {
            html += `<p style="color:#666;font-size:7pt;padding-left:8px;">Karsilayan: ${d.sponsorships.map((s) => `${translateYsSponsor(s.sponsor)} -${(s.amount || 0).toFixed(2)}`).join(' | ')}</p>`;
          }
        });
        html += `</div>`;
        return html;
      })()}
      <div class="grand-total">TOPLAM: ${getYsTotals(o).genelToplam.toFixed(2)} TL</div>
      ${isOnline ? '<div class="payment-info">** ONLINE ODENDI **</div>' : ''}
      ${o.collectFromCustomer > 0 ? `<div class="payment-info">MUSTERIDEN TAHSIL: ${o.collectFromCustomer.toFixed(2)} TL</div>` : ''}
      <div class="receipt-label">YEMEKSEPETI PAKET SIPARIS FISI</div>
      <div class="footer">
        ${receiptSettings?.standardFooterNote ? `<p>${receiptSettings.standardFooterNote}</p>` : ''}
        ${receiptSettings?.thankYouMessage ? `<p>${receiptSettings.thankYouMessage}</p>` : ''}
      </div>
    </body></html>`;
  }, [screenData]);

  const handlePrintReceipt = useCallback(async () => {
    if (!order) return;
    const printer = screenData?.printingSettings?.defaultReceiptPrinter
      || screenData?.printingSettings?.adisyonPrinter;
    if (!printer) { alert('Fis yazicisi tanimlanmamis.'); return; }

    setIsPrinting(true);
    try {
      const localServiceUrl = screenData?.localServiceUrl || 'http://localhost:3003';
      // Markaya özel fiş başlığı; marka tanımlı değilse ekran varsayılanı.
      const brandHeader = resolveReceiptHeader(buildReceiptOrderRef('yemeksepeti', null, order), screenData);
      const addrFull = [order.deliveryAddress, order.deliveryDistrict, order.deliveryCity].filter(Boolean).join(', ');
      const ptUpperPrint = (order.paymentType || '').toUpperCase().replace(/İ/g, 'I').replace(/Ş/g, 'S').replace(/Ğ/g, 'G');
      const isOnlinePaid = ptUpperPrint.includes('ONLINE') ||
        ptUpperPrint.includes('PAY_WITH_CARD') ||
        ptUpperPrint.startsWith('PAY_WITH');

      // `rawParsed` halen receiptData'daki addressDescription ve customerNote
      // alanları için gerekli — silinmemeli. Ürün satırı üretimi ise artık
      // helper'da yapılıyor (formatDiscountSubItems kaldırıldı).
      const rawParsed = parseRawJsonData(order.rawJsonData);
      // ✅ Ürün satırı üretimi `yemeksepetiReceiptBuilder` helper'ına taşındı —
      // `useKitchenPrintHandlers` (otomatik yazdırma) ile **aynı** helper'ı
      // kullanıyoruz. Helper modal'daki tüm zengin mantığı (variation,
      // toppingsJson + raw selectedToppings, selectedChoices, item-level
      // indirim + sponsorship breakdown, müşteri yorumu) birebir koruyor.
      const receiptItems = buildYemeksepetiReceiptItems(order);

      // v2026 uyum: payload'in `price` objesini birinci oncelikli kaynak kabul
      // eden 3 katmanli fallback ile dogru toplamlari hesapla. Eski (paidPrice
      // toppingli) ve yeni (paidPrice toppingsiz) format icin tek kod yolu.
      const totals = getYsTotals(order);
      const itemsTotal = totals.araToplam;
      const grandTotal = totals.genelToplam;
      const discountTotal = totals.indirimToplam;

      // createdAt 'Z' olmadan gelebilir; UTC olarak yorumlayip Europe/Istanbul'da
      // formatliyoruz ki fiste GMT+3 dogru yazilsin.
      const createdAtDate = parseAsUtcDate(order.createdAt);
      const printDate = createdAtDate
        ? new Intl.DateTimeFormat('tr-TR', {
            day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ISTANBUL_TZ,
          }).format(createdAtDate)
        : '';
      const printTime = createdAtDate
        ? new Intl.DateTimeFormat('tr-TR', {
            hour: '2-digit', minute: '2-digit', hour12: false, timeZone: ISTANBUL_TZ,
          }).format(createdAtDate)
        : '';

      // ─── Kurye konum QR'ı ──────────────────────────────────────────────
      // `rawJsonData.delivery.address.{latitude,longitude}` üzerinden Google
      // Maps konum URL'i üretip QR'a çeviriyoruz. `useKitchenPrintHandlers`
      // (oto-yazdırma) ile birebir aynı helper'ları (`parseYsRawJsonData` +
      // `buildGoogleMapsUrl`) kullanıyoruz; davranış garanti aynı kalır.
      const ysRawParsedFull = parseYsRawJsonData(order.rawJsonData);
      const mapsUrl = buildGoogleMapsUrl(ysRawParsedFull.latitude, ysRawParsedFull.longitude);
      // Gel Al siparişinde teslimat adresi yoktur; müşterinin geleceği saat
      // fişin NOT alanına yazılır (yerel yazıcı servisi ayrı bir alan basmıyor).
      const ysPickupTimeStr = formatYsPickupTime(ysRawParsedFull.pickupTime);
      const ysPickupNote = getYsDeliveryKind(order as any) === 'pickup'
        ? `GEL AL${ysPickupTimeStr ? ` - Teslim Alma Saati: ${ysPickupTimeStr}` : ''}`
        : undefined;
      let qrCodeDataUrl: string | undefined;
      if (mapsUrl) {
        try {
          qrCodeDataUrl = await QRCode.toDataURL(mapsUrl, {
            errorCorrectionLevel: 'M',
            margin: 1,
            width: 320,
          });
        } catch (qrErr) {
          console.warn('[YsDetail] QR uretilemedi:', qrErr);
        }
      }

      const receiptData = {
        items: receiptItems,
        orderNumber: order.shortCode || order.code || String(order.id),
        shortCode: order.shortCode || order.code || undefined,
        customerName: order.customerFullName || 'Müşteri',
        customerPhone: order.customerPhone || '',
        customerAddress: addrFull || undefined,
        addressDescription: rawParsed.deliveryInstructions || undefined,
        qrCodeDataUrl,
        qrCodeRawUrl: mapsUrl,
        qrCodeLabel: (qrCodeDataUrl || mapsUrl) ? 'ADRES QR' : undefined,
        date: printDate,
        time: printTime,
        grandTotal,
        subtotal: itemsTotal > grandTotal ? itemsTotal : grandTotal,
        discountTotal,
        vatTotal: 0,
        paymentStatus: isOnlinePaid ? 'PAID' : undefined,
        paymentMethod: isOnlinePaid ? 'Online Ödeme' : (order.paymentType || 'Kapıda Ödeme'),
        isOnlinePaid: isOnlinePaid,
        customerNote: ysPickupNote
          ? [ysPickupNote, order.customerComment || rawParsed.customerComment].filter(Boolean).join(' | ')
          : (order.customerComment || rawParsed.customerComment || undefined),
        ...brandHeader,
        logoMaxWidthPx: brandHeader.logoMaxWidthPx ?? 384,
        logoMaxHeightPx: brandHeader.logoMaxHeightPx ?? 150,
        receiptLabel: resolveReceiptLabel(
          order.isPreOrder ? 'YEMEKSEPETI - İLERİ TARİHLİ SİPARİŞ' : 'YEMEKSEPETI PAKET SIPARIS',
          buildReceiptOrderRef('yemeksepeti', null, order),
          screenData,
        ),
        deliveryType: getYsDeliveryLabel(order as any, 'receipt'),
        isPreOrder: order.isPreOrder,
        expectedDeliveryTime: order.expectedDeliveryTime,
      };
      await printPackageReceipt(receiptData, printer, localServiceUrl);
    } catch (err: any) {
      console.error('[YSDetail] Fis yazdirma hatasi:', err);
      alert(`Fis yazdirma hatasi: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      setIsPrinting(false);
    }
  }, [order, screenData]);

  const statusInfo = getStatusLabel(order?.status);
  const deliveryKind = getYsDeliveryKind(order as any);
  const isPickup = !!order && deliveryKind === 'pickup';
  const isCourierDelivery = !!order && deliveryKind === 'platformCourier';
  const isOnline = order ? ((order.paymentType || '').toUpperCase().replace(/İ/g, 'I').includes('ONLINE') || (order.paymentType || '').toUpperCase().includes('PAY_WITH_CARD') || (order.paymentType || '').toUpperCase().startsWith('PAY_WITH')) : false;
  const rawData = React.useMemo(() => parseRawJsonData(order?.rawJsonData), [order?.rawJsonData]);
  // Gel Al siparişinde `pickup.pickupTime` müşterinin restorana geleceği saattir.
  const pickupTimeLabel = React.useMemo(
    () => (isPickup ? formatYsPickupTime(parseYsRawJsonData(order?.rawJsonData).pickupTime) : undefined),
    [isPickup, order?.rawJsonData],
  );
  // v2026 uyum: Topping fiyati paidPrice'a dahil mi? Order seviyesinde tek
  // seferde karar verip her item satirinda tutarli kullaniyoruz.
  const paidPriceIncludesToppings = React.useMemo(
    () => (order ? detectPaidPriceIncludesToppings(order) : true),
    [order],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: '#e31e24', color: 'white', py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <RestaurantMenuIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">Yemeksepeti Siparis Detayi</Typography>
            {order && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                #{order.shortCode || order.code || order.id}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#e31e24' }} />
          </Box>
        )}
        {error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" variant="h6">{error}</Typography>
          </Box>
        )}

        {order && !loading && (
          <>
            {isPickup && (
              <Box sx={{
                bgcolor: '#2E7D32', color: 'white', p: 2, mb: 2, borderRadius: 2,
                display: 'flex', alignItems: 'center', gap: 2,
                border: '2px solid #1B5E20',
              }}>
                <ShoppingBagIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">GEL AL SIPARISI</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    Musteri siparisi restorandan kendisi teslim alacak
                    {pickupTimeLabel ? ` — Teslim alma saati: ${pickupTimeLabel}` : ''}
                  </Typography>
                </Box>
              </Box>
            )}
            {order.isPreOrder && order.expectedDeliveryTime && (
              <Box sx={{
                bgcolor: '#ff6f00', color: 'white', p: 2, mb: 2, borderRadius: 2,
                display: 'flex', alignItems: 'center', gap: 2,
                border: '2px solid #e65100',
              }}>
                <Box sx={{ fontSize: 40 }}>⏰</Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold">İLERİ TARİHLİ SİPARİŞ</Typography>
                  <Typography variant="h5" fontWeight="bold">
                    Teslim: {(() => {
                      const utcStr = order.expectedDeliveryTime!;
                      const utcDate = utcStr.endsWith('Z') ? new Date(utcStr) : new Date(utcStr + 'Z');
                      const trMs = utcDate.getTime() + 3 * 60 * 60 * 1000;
                      const trDate = new Date(trMs);
                      const h = String(trDate.getUTCHours()).padStart(2, '0');
                      const m = String(trDate.getUTCMinutes()).padStart(2, '0');
                      const d = String(trDate.getUTCDate()).padStart(2, '0');
                      const mo = String(trDate.getUTCMonth() + 1).padStart(2, '0');
                      return `${d}.${mo} ${h}:${m}`;
                    })()}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">DURUM</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                </Box>
              </Paper>
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">SIPARIS ZAMANI</Typography>
                <Typography variant="h6" fontWeight="bold">{formatTime(order.createdAt)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateOnly(order.createdAt)}
                </Typography>
              </Paper>
              <Paper
                elevation={2}
                sx={{
                  p: 2, flex: 1, minWidth: 160,
                  bgcolor: isPickup ? '#E8F5E9' : isCourierDelivery ? '#E3F2FD' : '#FFF3E0',
                  border: isPickup ? '2px solid #2E7D32' : undefined,
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight="bold">TESLIMAT TIPI</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {isPickup
                    ? <ShoppingBagIcon color="success" />
                    : isCourierDelivery ? <LocalShippingIcon color="info" /> : <StorefrontIcon color="warning" />}
                  <Typography variant="body1" fontWeight="bold">
                    {getYsDeliveryLabel(order as any)}
                  </Typography>
                </Box>
                {isPickup && pickupTimeLabel && (
                  <Typography variant="caption" color="success.dark" fontWeight="bold">
                    Teslim alma saati: {pickupTimeLabel}
                  </Typography>
                )}
              </Paper>
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">ODEME</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {isOnline ? 'Online Odeme' : (order.paymentType || 'Kapida Odeme')}
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Musteri Bilgileri</Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Ad Soyad</Typography>
                  <Typography variant="body1" fontWeight="bold">{order.customerFullName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Telefon</Typography>
                  <Typography variant="body1">{order.customerPhone || '-'}</Typography>
                </Grid>
                {!isPickup && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Adres</Typography>
                    <Typography variant="body1">
                      {[order.deliveryAddress, order.deliveryDistrict, order.deliveryCity].filter(Boolean).join(', ') || '-'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {isPickup && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#E8F5E9', borderRadius: 1, border: '1px solid #2E7D32' }}>
                  <Typography variant="body2" color="success.dark" fontWeight="bold">
                    Gel Al siparisi — teslimat adresi yok, musteri siparisi restorandan kendisi teslim alacak.
                    {pickupTimeLabel ? ` Teslim alma saati: ${pickupTimeLabel}.` : ''}
                  </Typography>
                </Box>
              )}
              {(order.customerComment || rawData.customerComment) && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="warning.dark" fontWeight="medium">
                    Siparis Notu: {order.customerComment || rawData.customerComment}
                  </Typography>
                </Box>
              )}
              {rawData.deliveryInstructions && rawData.deliveryInstructions !== order.customerComment && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px dashed #e65100' }}>
                  <Typography variant="body2" color="#e65100" fontWeight="bold">
                    Teslimat Talimati: {rawData.deliveryInstructions}
                  </Typography>
                </Box>
              )}
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Siparis Kalemleri ({order.items?.length || 0} urun)
            </Typography>
            <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 2 }}>
              {order.items?.map((item, index) => {
                const rawProduct = rawData.products[index];
                const dbToppings: any[] = (() => {
                  try { return item.toppingsJson ? JSON.parse(item.toppingsJson) : []; } catch { return []; }
                })();
                const rawToppings = rawProduct?.selectedToppings || [];
                const rawChoices = rawProduct?.selectedChoices || [];
                const toppings = dbToppings.length > 0 ? dbToppings : rawToppings;

                // Yemeksepeti v2026: Topping/secim altinda eger yeni payload ile
                // discounts geliyorsa kucuk bir indirim satiri renderlanir.
                const renderDiscountLine = (
                  discounts: DiscountWithSponsorship[] | undefined,
                  depth: number,
                  keyPrefix: string,
                ) => {
                  if (!discounts || discounts.length === 0) return null;
                  return discounts.map((d, di) => (
                    <Box key={`${keyPrefix}-d${di}`} sx={{ pl: depth * 1.5 }}>
                      <Typography variant="caption" color="error.main" component="div">
                        Indirim: {translateYsDiscountName(d.name)} -{(d.amount || 0).toFixed(2)} TL
                      </Typography>
                      {d.sponsorships && d.sponsorships.length > 0 && (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 1.5, fontSize: '0.7rem' }}>
                          {d.sponsorships.map((s) => `${translateYsSponsor(s.sponsor)}: -${(s.amount || 0).toFixed(2)}`).join(' | ')}
                        </Typography>
                      )}
                    </Box>
                  ));
                };

                const renderOption = (opt: any, prefix: string, depth: number = 0) => {
                  const name = opt.name || opt.Name || '';
                  const price = parseFloat(opt.price ?? opt.Price ?? '0');
                  const qty = opt.quantity || opt.Quantity || 1;
                  const children: any[] = opt.children || opt.Children || [];
                  // v2026 topping discount: normalize edilmis option'da `discounts` olabilir.
                  // Henuz normalize edilmemis (DB toppingsJson) ise ham payload'dan defansif okuma.
                  const optDiscountsRaw: any[] = Array.isArray(opt.discounts)
                    ? opt.discounts
                    : Array.isArray(opt.Discounts)
                      ? opt.Discounts
                      : [];
                  const optDiscounts: DiscountWithSponsorship[] = optDiscountsRaw.length > 0
                    ? optDiscountsRaw.map(normalizeDiscount)
                    : [];
                  return (
                    <Box key={`${prefix}-${name}`} sx={{ pl: depth * 1.5 }}>
                      <Typography variant="caption" color={price > 0 ? 'primary.main' : 'text.secondary'} component="div">
                        {depth > 0 ? '  ' : ''}{qty > 1 ? `${qty}x ` : ''}+ {name}
                        {price > 0 ? ` (+${price.toFixed(2)} TL)` : ''}
                      </Typography>
                      {renderDiscountLine(optDiscounts, depth + 1, `${prefix}-${name}`)}
                      {children.map((child: any, ci: number) => renderOption(child, `${prefix}-c${ci}`, depth + 1))}
                    </Box>
                  );
                };

                // Yemeksepeti v2026: backend kolonu `discountsJson` ya da raw JSON product.discounts'tan urun-level indirimleri al.
                const itemDiscounts = getItemDiscounts(item, rawProduct);

                return (
                  <Paper key={item.id || index} elevation={1} sx={{ p: 2, mb: 1.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="bold">{item.name}</Typography>
                      {item.variationName && item.variationName !== item.name && (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic' }}>
                          {item.variationName}
                        </Typography>
                      )}
                      {toppings.length > 0 && (
                        <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid #e0e0e0' }}>
                          {toppings.map((t: any, ti: number) => renderOption(t, `t${ti}`))}
                        </Box>
                      )}
                      {rawChoices.length > 0 && (
                        <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid #bbdefb' }}>
                          <Typography variant="caption" color="info.main" fontWeight="bold" component="div">Secimler:</Typography>
                          {rawChoices.map((c: any, ci: number) => renderOption(c, `ch${ci}`))}
                        </Box>
                      )}
                      {/* Yemeksepeti v2026: Urun-level indirim + sponsorship breakdown */}
                      {itemDiscounts.length > 0 && (
                        <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid #ffcdd2' }}>
                          {itemDiscounts.map((d, di) => (
                            <Box key={`item-${item.id}-d${di}`} sx={{ mb: 0.5 }}>
                              <Typography variant="caption" color="error.main" fontWeight="bold" component="div">
                                Indirim: {translateYsDiscountName(d.name)} -{(d.amount || 0).toFixed(2)} TL
                              </Typography>
                              {d.sponsorships && d.sponsorships.length > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 1, fontSize: '0.7rem' }}>
                                  Karsilayan: {d.sponsorships.map((s) => `${translateYsSponsor(s.sponsor)} -${(s.amount || 0).toFixed(2)}`).join(' | ')}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                      {(item.comment || rawProduct?.comment) && (
                        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                          Not: {item.comment || rawProduct?.comment}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                      {(() => {
                        // v2026: originalUnitPrice "indirim oncesi orijinal fiyat" semantigi.
                        // Eski siparislerde null gelir → unitPrice'a fallback. Boylece
                        // "2 x 6.00 = 10.50" tutarsizligi yeni veride dogru anlatilir.
                        const original = item.originalUnitPrice ?? item.unitPrice ?? 0;
                        const totalPaid = item.paidPrice || 0;
                        // v2026 uyum: Yeni format payload'da paidPrice toppingleri
                        // icermez. Satir toplamini topping'i de ekleyerek hesapla.
                        // Eski formatta `paidPriceIncludesToppings = true` doner ve
                        // sadece paidPrice gosterilir (mevcut davranis korunur).
                        const toppingTotalPerProduct = (toppings as any[]).reduce(
                          (s: number, t: any) =>
                            s + (parseFloat(t?.price ?? '0') || 0) * (Number(t?.quantity) || 1),
                          0,
                        );
                        const lineTotal = paidPriceIncludesToppings
                          ? totalPaid
                          : totalPaid + toppingTotalPerProduct * (item.quantity || 1);
                        const expectedTotal = original * (item.quantity || 1)
                          + (paidPriceIncludesToppings ? 0 : toppingTotalPerProduct * (item.quantity || 1));
                        const hasDiscount = expectedTotal > lineTotal + 0.01;
                        return (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {item.quantity} x {original.toFixed(2)} TL
                            </Typography>
                            {hasDiscount && (
                              <Typography variant="caption" color="error.main" component="div" sx={{ fontSize: '0.7rem' }}>
                                (indirim sonrasi)
                              </Typography>
                            )}
                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                              {lineTotal.toFixed(2)} TL
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  </Paper>
                );
              })}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              {(() => {
                // Yemeksepeti v2026 uyumlu toplam hesabi: payload'in `price`
                // objesini birinci oncelikli kaynak kabul eden 3 katmanli
                // fallback. Eski (paidPrice toppingli) ve yeni (paidPrice
                // toppingsiz) formatlarin ikisi de tek kod yolu ile destekleniyor.
                const totals = getYsTotals(order);
                const orderDiscounts = getOrderDiscounts(order);
                // Indirim satirini gosterme kosulu: hesaplanan indirim > 0
                // VEYA payload'da yapısal indirim listesi var.
                const showDiscountSection = totals.indirimToplam > 0.01 || orderDiscounts.length > 0;
                // Yapısal indirim listesi varsa onların toplamı; yoksa
                // hesaplanan farki goster ("Platform Indirimi" fallback).
                const structuredDiscountsSum = orderDiscounts.reduce(
                  (s, d) => s + (Number(d.amount) || 0), 0,
                );
                const fallbackDiscount = structuredDiscountsSum > 0.01
                  ? structuredDiscountsSum
                  : totals.indirimToplam;
                return (
                  <>
                    {showDiscountSection && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body1" color="text.secondary">Ara Toplam</Typography>
                          <Typography variant="body1">{totals.araToplam.toFixed(2)} TL</Typography>
                        </Box>
                        {orderDiscounts.length > 0 ? (
                          orderDiscounts.map((d, i) => (
                            <Box key={i} sx={{ mb: 0.75 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body1" color="error.main" fontWeight="bold">
                                  {translateYsDiscountName(d.name)}
                                </Typography>
                                <Typography variant="body1" color="error.main" fontWeight="bold">
                                  -{(d.amount || 0).toFixed(2)} TL
                                </Typography>
                              </Box>
                              {/* v2026: Sponsorship breakdown - karsilayan dokumu */}
                              {d.sponsorships && d.sponsorships.length > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ pl: 1, mt: 0.25, fontSize: '0.72rem' }}>
                                  Karsilayan: {d.sponsorships.map((s) => `${translateYsSponsor(s.sponsor)} -${(s.amount || 0).toFixed(2)} TL`).join(' | ')}
                                </Typography>
                              )}
                            </Box>
                          ))
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body1" color="error.main" fontWeight="bold">Platform İndirimi</Typography>
                            <Typography variant="body1" color="error.main" fontWeight="bold">
                              -{fallbackDiscount.toFixed(2)} TL
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid', borderColor: 'divider' }}>
                      <Typography variant="h6" fontWeight="bold">TOPLAM</Typography>
                      <Typography variant="h5" fontWeight="bold" color="success.main">
                        {totals.genelToplam.toFixed(2)} TL
                      </Typography>
                    </Box>
                  </>
                );
              })()}
              {order.collectFromCustomer > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Musteriden Tahsil</Typography>
                  <Typography variant="body1" fontWeight="bold" color="warning.main">
                    {order.collectFromCustomer.toFixed(2)} TL
                  </Typography>
                </Box>
              )}
            </Paper>

            {order.assignedCourierName && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Kurye Bilgileri</Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                  <Typography variant="body1" fontWeight="bold">{order.assignedCourierName}</Typography>
                </Paper>
              </>
            )}

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Zaman Cizelgesi</Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Olusturulma</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatDate(order.createdAt)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Son Guncelleme</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatDate(order.updatedAt)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            onClick={handlePrintReceipt}
            disabled={!order || isPrinting}
            sx={{ bgcolor: '#e31e24', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {isPrinting ? 'Yazdırılıyor...' : 'Fiş Yazdır'}
          </Button>
          {/* Online ödenmiş siparişler için Pavo cihazından mali fiş kestirme.
              Yalnızca onRequestOnlineReceipt prop'u tanımlı VE sipariş online
              ödenmiş ise butonu göster. */}
          {onRequestOnlineReceipt && order && isOnline && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ReceiptLongIcon />}
              onClick={onRequestOnlineReceipt}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Online Fiş Yazdır
            </Button>
          )}
        </Box>
        <Button variant="outlined" onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default YemeksepetiOrderDetailModal;
